import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Col,
  Row,
  Button,
  Alert,
  Form,
  Spinner,
  Card,
} from "react-bootstrap";

import "./ForgotPassword.css";
import { put } from "utils/BeeApi";
import { isValidEmail } from "utils/StringUtils";
import ErrorHandler from "components/ErrorHandler/ErrorHandler";
import TextLogo from "components/App/TextLogo/TextLogo";
import RedirectIfAuthenticated from "../RedirectIfAuthenticated/RedirectIfAuthenticated";
import Copyright from "../Copyright/Copyright";

const ForgotPassword = () => {
  const [email, setEmail] = useState();
  const [emailValid, setEmailValid] = useState();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [message, setMessage] = useState();
  const subscribedPromises = useRef([]);

  useEffect(() => {
    const promises = subscribedPromises.current;
    return () => promises.forEach((promise) => promise.cancel());
  }, []);

  const handleEmailChange = (event) => {
    let _email = event.target.value;
    setEmail(_email);
    setEmailValid(isValidEmail(_email));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormSubmitted(true);
    if (emailValid) {
      setError("");
      setIsLoading(true);

      const createPromise = put("/login", {
        action: "forgot",
        email: email,
      });
      createPromise.promise
        .then((response) => {
          setError("");
          setIsLoading(false);
          setSuccess(true);
          setMessage(response?.error?.message);
        })
        .catch((error) => {
          setError(error);
          setIsLoading(false);
        });

      subscribedPromises.current.push(createPromise);
    }
  };

  const renderForgotPasswordForm = () => (
    <form onSubmit={handleSubmit}>
      <div className="mt-sm mb-md text-center">
        <h2 className="pre-title">Request New Password</h2>
        <p>We&#x27;ll email you instructions to set your password.</p>
      </div>
      <hr />
      <Form.Group className="mb-3">
        <Form.Label>Email address</Form.Label>
        <Form.Control
          type="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="Enter email"
          isInvalid={!emailValid && formSubmitted}
          autoFocus
        />
        <Form.Control.Feedback type="invalid">
          This should be a valid email address
        </Form.Control.Feedback>
      </Form.Group>
      {error && <ErrorHandler error={error} />}
      {success && (
        <Alert variant={message ? "warning" : "success"}>
          <h4>{message ? "Warning" : "Success"}!</h4>
          <p>
            {message
              ? "You have not been invited, please contact administrator at: team@angelcatalyst.com"
              : "Please follow the instructions sent to the email you provided to reset your password."}
          </p>
        </Alert>
      )}
      <div className="d-grid gap-2 mb-3">
        <Button
          className="mb-4 text-white"
          type="submit"
          color="primary"
          disabled={isLoading}
        >
          {isLoading && (
            <Spinner
              className="me-2"
              animation="border"
              size="sm"
              variant="light"
            />
          )}
          Send Instructions
        </Button>
      </div>
      <p className="text-center">
        <Link to="/login">Cancel</Link>
      </p>
    </form>
  );

  return (
    <Container className="ForgotPassword mt-4 mb-4">
      <RedirectIfAuthenticated />
      <Row>
        <Col
          xl={{ span: 4, offset: 4 }}
          lg={{ span: 4, offset: 4 }}
          md={{ span: 6, offset: 3 }}
          sm={{ span: 8, offset: 2 }}
        >
          <TextLogo />
          <Card className="border-0 shadow-sm">
            <Card.Body>{renderForgotPasswordForm()}</Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col
          xl={{ span: 4, offset: 4 }}
          lg={{ span: 4, offset: 4 }}
          md={{ span: 6, offset: 3 }}
          sm={{ span: 8, offset: 2 }}
        >
          <p className="text-dark text-center my-2">
            <small>
              <Copyright />
            </small>
          </p>
          <p className="text-dark text-center">Powered by PixelEdge.io</p>
        </Col>
      </Row>
    </Container>
  );
};

export default ForgotPassword;
